/**
 * Subject detail page - view/edit attributes and audit trail
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  User,
  Pencil,
  Trash2,
  Plus,
  History,
  ScrollText,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { useSubject, useCreateSubject, useDeleteSubject } from '@/hooks/use-subjects'
import {
  useSubjectAttributes,
  useSetAttribute,
  useDeleteAttribute,
  useAttributeHistory,
} from '@/hooks/use-attribute-values'
import { useSubjectAuditLogs } from '@/hooks/use-uas-audit-logs'
import type { SubjectStatus } from '@/types/uas'

export function SubjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const isNew = id === 'new'
  const subjectId = isNew ? undefined : id

  const { data: subject, isLoading: subjectLoading } = useSubject(subjectId)
  const { data: attributes, isLoading: attributesLoading } = useSubjectAttributes(subjectId)
  const { data: auditLogs, isLoading: auditLogsLoading } = useSubjectAuditLogs(subjectId)

  const createSubjectMutation = useCreateSubject()
  const deleteSubject = useDeleteSubject()
  const setAttribute = useSetAttribute(subjectId ?? '')
  const deleteAttribute = useDeleteAttribute(subjectId ?? '')

  const [editingAttr, setEditingAttr] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [newAttrKey, setNewAttrKey] = useState('')
  const [newAttrValue, setNewAttrValue] = useState('')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [historyKey, setHistoryKey] = useState<string | null>(null)

  // Create form state
  const [newIdentifier, setNewIdentifier] = useState('')
  const [newIdentifierType, setNewIdentifierType] = useState('EMAIL')
  const [newDisplayName, setNewDisplayName] = useState('')
  const [newEmail, setNewEmail] = useState('')

  const { data: historyData } = useAttributeHistory(subjectId, historyKey ?? undefined)

  const handleSaveAttribute = (key: string) => {
    setAttribute.mutate(
      { key, value: editValue, dataType: 'STRING', source: 'MANUAL', confidence: 1.0 },
      { onSuccess: () => setEditingAttr(null) }
    )
  }

  const handleAddAttribute = () => {
    if (!newAttrKey.trim() || !newAttrValue.trim()) return
    setAttribute.mutate(
      { key: newAttrKey, value: newAttrValue, dataType: 'STRING', source: 'MANUAL', confidence: 1.0 },
      {
        onSuccess: () => {
          setNewAttrKey('')
          setNewAttrValue('')
          setAddDialogOpen(false)
        },
      }
    )
  }

  const handleCreateSubject = () => {
    if (!newIdentifier.trim()) return
    createSubjectMutation.mutate({
      primaryIdentifier: newIdentifier,
      primaryIdentifierType: newIdentifierType as 'EMAIL' | 'EMPLOYEE_ID' | 'EXTERNAL_ID' | 'USERNAME',
      displayName: newDisplayName || undefined,
      email: newEmail || undefined,
    })
  }

  if (isNew) {
    return (
      <>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/identity/subjects')}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Subjects
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <Plus className="h-4 w-4" />
          <h1 className="text-base font-medium">New Subject</h1>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 md:p-6 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Create Subject</CardTitle>
              <CardDescription>
                Add a new subject identity to the attribute store
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Primary Identifier *</Label>
                <Input
                  placeholder="e.g. jane.doe@company.com"
                  value={newIdentifier}
                  onChange={(e) => setNewIdentifier(e.target.value)}
                />
              </div>
              <div>
                <Label>Identifier Type</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={newIdentifierType}
                  onChange={(e) => setNewIdentifierType(e.target.value)}
                >
                  <option value="EMAIL">Email</option>
                  <option value="EMPLOYEE_ID">Employee ID</option>
                  <option value="EXTERNAL_ID">External ID</option>
                  <option value="USERNAME">Username</option>
                </select>
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  placeholder="e.g. Jane Doe"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="e.g. jane.doe@company.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCreateSubject}
                  disabled={!newIdentifier.trim() || createSubjectMutation.isPending}
                >
                  {createSubjectMutation.isPending ? 'Creating...' : 'Create Subject'}
                </Button>
                <Button variant="outline" onClick={() => navigate('/identity/subjects')}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    )
  }

  if (subjectLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!subject) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="text-muted-foreground">Subject not found.</p>
      </div>
    )
  }

  const statusVariant: Record<SubjectStatus, 'default' | 'secondary' | 'destructive'> = {
    ACTIVE: 'default',
    INACTIVE: 'secondary',
    SUSPENDED: 'destructive',
  }

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/identity/subjects')}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Subjects
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <User className="h-4 w-4" />
          <h1 className="text-base font-medium">{subject.displayName ?? subject.primaryIdentifier}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => deleteSubject.mutate(subject.id)}
            disabled={subject.status === 'INACTIVE'}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Deactivate
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        {/* Subject Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Subject Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Identifier</Label>
                <p className="text-sm font-mono">{subject.primaryIdentifier}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Type</Label>
                <p className="text-sm">{subject.primaryIdentifierType}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <div className="mt-0.5">
                  <Badge variant={statusVariant[subject.status]}>{subject.status}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Display Name</Label>
                <p className="text-sm">{subject.displayName ?? '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <p className="text-sm">{subject.email ?? '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Source</Label>
                <p className="text-sm">{subject.sourceIdpId ? 'IdP Sync' : 'Manual'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Attributes | History | Audit */}
        <Tabs defaultValue="attributes">
          <TabsList>
            <TabsTrigger value="attributes">Attributes</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="audit">Audit Trail</TabsTrigger>
          </TabsList>

          <TabsContent value="attributes" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Current Attributes</CardTitle>
                    <CardDescription>Active attribute values for this subject</CardDescription>
                  </div>
                  <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="mr-2 h-3.5 w-3.5" />
                        Add Attribute
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Attribute</DialogTitle>
                        <DialogDescription>Set a new attribute value for this subject</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Attribute Key</Label>
                          <Input
                            placeholder="e.g. subject.department"
                            value={newAttrKey}
                            onChange={(e) => setNewAttrKey(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Value</Label>
                          <Input
                            placeholder="e.g. Engineering"
                            value={newAttrValue}
                            onChange={(e) => setNewAttrValue(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddAttribute} disabled={setAttribute.isPending}>
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {attributesLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : !attributes || attributes.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No attributes set for this subject.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Key</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Since</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attributes.map((attr) => (
                        <TableRow key={attr.id}>
                          <TableCell className="font-mono text-xs">{attr.attributeKey}</TableCell>
                          <TableCell>
                            {editingAttr === attr.attributeKey ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="h-7 text-xs"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7"
                                  onClick={() => handleSaveAttribute(attr.attributeKey)}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7"
                                  onClick={() => setEditingAttr(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <span className="text-sm">
                                {typeof attr.value === 'object'
                                  ? JSON.stringify(attr.value)
                                  : String(attr.value)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {attr.dataType}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {attr.source}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(attr.validFrom).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  setEditingAttr(attr.attributeKey)
                                  setEditValue(
                                    typeof attr.value === 'object'
                                      ? JSON.stringify(attr.value)
                                      : String(attr.value)
                                  )
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setHistoryKey(attr.attributeKey)}
                              >
                                <History className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive"
                                onClick={() => deleteAttribute.mutate(attr.attributeKey)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Attribute History</CardTitle>
                <CardDescription>
                  {historyKey
                    ? `Temporal versions of "${historyKey}"`
                    : 'Select an attribute to view its history'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!historyKey ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    Click the history icon on an attribute to view its version timeline.
                  </p>
                ) : !historyData || historyData.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No history found for this attribute.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Value</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Valid From</TableHead>
                        <TableHead>Valid To</TableHead>
                        <TableHead>Confidence</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm">
                            {typeof entry.value === 'object'
                              ? JSON.stringify(entry.value)
                              : String(entry.value)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {entry.source}
                          </TableCell>
                          <TableCell className="text-xs">
                            {new Date(entry.validFrom).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs">
                            {entry.validTo
                              ? new Date(entry.validTo).toLocaleString()
                              : 'Current'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {(entry.confidence * 100).toFixed(0)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ScrollText className="h-4 w-4" />
                  <CardTitle>Audit Trail</CardTitle>
                </div>
                <CardDescription>All changes to this subject</CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : !auditLogs || auditLogs.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No audit records for this subject.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>Attribute</TableHead>
                        <TableHead>Old Value</TableHead>
                        <TableHead>New Value</TableHead>
                        <TableHead>Actor</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {log.attributeKey ?? '-'}
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate text-xs text-muted-foreground">
                            {log.oldValue != null ? JSON.stringify(log.oldValue) : '-'}
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate text-xs">
                            {log.newValue != null ? JSON.stringify(log.newValue) : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {log.actorType}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(log.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
